import { Component } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Data } from '@angular/router';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { settings } from 'src/app/models_new/config/application-settings';
import { IOrganizationContextResolverData } from 'src/app/resolvers/organization-context-resolver.resolver';
import {
  InstalledRobotsApiService,
  PreviewUrl,
} from 'src/app/services/api/robot-installations-api.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-psx-preview',
  templateUrl: './psx-preview.component.html',
  styleUrls: ['./psx-preview.component.scss'],
})
export class PsxPreviewComponent {
  polyscopeVersion: string = settings.psxPreviewPolyscopeVersion;
  robot: string = settings.psxPreviewRobotType;

  org_id$: Observable<string>;
  urSimUrl$: Observable<SafeResourceUrl | null>;
  starting: boolean = false;

  constructor(
    private robotsApi: InstalledRobotsApiService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private notify: NotificationService
  ) {
    this.org_id$ = this.route.data.pipe(
      map(
        (data: Data) =>
          (data as IOrganizationContextResolverData).organization_id
      )
    );

    this.urSimUrl$ = this.robotsApi
      .getPreviewUrls(`${this.polyscopeVersion}`)
      .pipe(
        switchMap((preview_url: PreviewUrl) => {
          console.debug('Preview URL received:', preview_url);
          if (preview_url === 'pending') {
            this.starting = true; // If the preview URL is pending, we assume we are starting the URSim
            return of(null); // Emit null while waiting for the URL to be ready
          } else if (preview_url === 'error') {
            this.starting = false; // If there is an error, we stop the loading state
            this.notify.showError(
              'Failed to retrieve preview URL. Please try again later.'
            );
            return of(null); // Emit null if there is an error
          } else if (preview_url === 'deleted') {
            this.starting = false; // If the preview URL is deleted, we stop the loading state
            this.notify.showError(
              'Preview URL has been deleted. Please start a new URSim.'
            );
            return of(null); // Emit null if the URL has been deleted
          } else if (preview_url) {
            this.starting = false; // If we have a valid preview URL, we stop the loading state
            return of(
              this.sanitizer.bypassSecurityTrustResourceUrl(preview_url)
            );
          } else {
            this.starting = false; // No preview URL available, stop the loading state
            return of(null); // Emit null if no preview URL is available
          }
        })
      );
  }

  downloadUrPx() {
    // Download file from local path
    const urPxPath = 'assets/downloads/PallyDemo.urpx';
    const link = document.createElement('a');
    link.href = urPxPath;
    link.download = 'PallyDemo.urpx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.notify.showMessage('Download started for URPx file.');
  }

  downloadUrCap() {
    // Download URCap from a remote URL
    const urCapUrl =
      'https://rfno17cd65f876.blob.core.windows.net/files/pally-1.1.0.urcapx?sp=r&st=2025-06-04T11:21:26Z&se=2026-05-01T19:21:26Z&spr=https&sv=2024-11-04&sr=b&sig=nQcf3TWGcspYcmHp35Q%2B3Gd9YIQa3oSpd7FcZa23ook%3D';
    const link = document.createElement('a');
    link.href = urCapUrl;
    link.download = 'pally-1.1.0.urcapx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.notify.showMessage('Download started for URCapX file.');
  }

  startUrSimContainer() {
    this.starting = true;
    this.org_id$
      .pipe(
        take(1),
        switchMap((org_id: string) =>
          this.robotsApi.insertPreviewUrl(
            org_id,
            this.polyscopeVersion,
            this.robot
          )
        ),
        take(1)
      )
      .subscribe({
        next: (res) => {
          console.debug('Preview URL inserted:', res);
          this.notify.showSuccess('URSim is starting, please wait a moment.');
          this.starting = false;
        },
        error: (err) => {
          console.error('Error inserting preview URL:', err);
          this.notify.showError(
            'Failed to start URSim. Please try again later.'
          );
          this.starting = false;
        },
      });
  }
}
